import React, { useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { FileText, Upload, CheckCircle2, Eye, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { DOC_STATUS_META } from '../../lib/kyc';

const MAX_UPLOAD = 10 * 1024 * 1024;
const ACCEPTED = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

export const openSecureDoc = async (api, url) => {
  try {
    const res = await api.get(url.replace(/^\/api/, ''), { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    window.open(blobUrl, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch { toast.error('Unable to open document'); }
};

// One card per required/optional KYC document: upload / progress / view / replace
export const DocumentCard = ({ item, canUpload, onUploaded }) => {
  const { api } = useAuth();
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const doc = item.document;
  const flagged = doc && ['rejected', 'reupload_required'].includes(doc.status);
  const statusMeta = doc ? (DOC_STATUS_META[doc.status] || DOC_STATUS_META.uploaded) : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return toast.error('Only PDF, JPG, JPEG, PNG or WEBP files are allowed');
    if (file.size > MAX_UPLOAD) return toast.error('File too large. Maximum size is 10MB');
    const fd = new FormData();
    fd.append('doc_type', item.doc_type);
    fd.append('file', file);
    setProgress(0);
    try {
      const res = await api.post('/merchants/me/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded * 100) / ev.total)),
      });
      toast.success(`${item.label} uploaded successfully`);
      onUploaded?.(res.data.document);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setProgress(null); }
  };

  return (
    <div className={`p-4 border rounded-sm flex flex-col gap-3 transition-colors ${flagged ? 'border-orange-500/50 bg-orange-500/5' : doc ? 'border-green-500/30 bg-green-500/5' : 'border-border/60'}`}
      data-testid={`doc-card-${item.doc_type}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2 rounded-sm shrink-0 ${doc ? 'bg-green-500/10 text-green-600' : 'bg-muted text-muted-foreground'}`}>
            {doc ? <CheckCircle2 className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
              {item.label}
              <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${item.required ? 'border-red-500/30 text-red-500' : 'border-border text-muted-foreground'}`}>
                {item.required ? 'Required' : 'Optional'}
              </span>
            </p>
            {doc ? (
              <>
                <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`doc-file-${item.doc_type}`}>
                  {doc.original_filename} · {Math.max(1, Math.round((doc.size || 0) / 1024))} KB
                </p>
                <p className={`text-xs mt-0.5 ${statusMeta.cls}`} data-testid={`doc-status-${item.doc_type}`}>
                  {flagged && <AlertTriangle className="inline h-3 w-3 mr-1" />}
                  {flagged ? statusMeta.label : `Uploaded successfully · ${statusMeta.label}`}
                  {doc.status_reason && flagged && <span className="block text-muted-foreground">Reason: {doc.status_reason}</span>}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">Accepted: PDF, JPG, JPEG, PNG, WEBP · Max 10MB</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {doc && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openSecureDoc(api, doc.view_url)} data-testid={`doc-view-${item.doc_type}`}>
              <Eye className="h-4 w-4 mr-1" /> View
            </Button>
          )}
          {canUpload && (
            <>
              <input ref={inputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleFile} data-testid={`upload-${item.doc_type}`} />
              <Button size="sm" variant={doc ? 'outline' : 'default'} className="rounded-sm" disabled={progress !== null} onClick={() => inputRef.current?.click()} data-testid={`upload-btn-${item.doc_type}`}>
                {progress !== null ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : doc ? <RefreshCw className="h-4 w-4 mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                {progress !== null ? 'Uploading' : doc ? 'Replace' : 'Upload Document'}
              </Button>
            </>
          )}
        </div>
      </div>
      {progress !== null && <Progress value={progress} className="h-1" data-testid={`upload-progress-${item.doc_type}`} />}
    </div>
  );
};
